import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PostCard from '../components/PostCard';
import Sidebar from '../components/Sidebar';
import { Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const [posts, setPosts] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query) {
      fetchSearchResults();
    }
  }, [query]);

  const fetchSearchResults = async () => {
    setLoading(true);
    try {
      const [searchRes, popularRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/search?q=${encodeURIComponent(query)}`),
        axios.get(`${API}/posts/popular?limit=5`),
        axios.get(`${API}/categories`),
      ]);

      setPosts(searchRes.data);
      setPopularPosts(popularRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-bold" data-testid="loading-indicator">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 py-16 sm:py-24">
        <div className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Search className="w-6 h-6 text-primary" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary" data-testid="search-label">
              Search Results
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl tracking-tight leading-none font-black" data-testid="search-query">
            "{query}"
          </h1>
          <p className="mt-4 text-muted-foreground" data-testid="search-count">
            {posts.length} {posts.length === 1 ? 'result' : 'results'} found
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          <div className="lg:col-span-2" data-testid="search-results-section">
            {posts.length > 0 ? (
              <div className="space-y-12">
                {posts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12" data-testid="no-results-message">
                <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-2xl font-bold mb-2">No results found</h2>
                <p className="text-muted-foreground">
                  Try different keywords or browse our categories to find what you're looking for.
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Sidebar popularPosts={popularPosts} categories={categories} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
